/**
 * User Transactions hook using React Relay + React Query
 *
 * Fetches wallet transactions and charges for a user separately from the main
 * user query, filtered by systemId. Intended to be called only when the
 * Transactions tab is active.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useQuery } from '@tanstack/react-query';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useUserTransactionsQueryQuery as UseUserTransactionsQueryQuery,
  WalletTransactionFilterInput,
  ChargeFilterInput,
} from './__generated__/useUserTransactionsQueryQuery.graphql';
import type { WalletTransactionGraphQL, ChargeGraphQL } from './useUserDetailRelay';

const UserTransactionsQuery = graphql`
  query useUserTransactionsQueryQuery(
    $filter: UserFilterInput!
    $walletTransactionFilter: WalletTransactionFilterInput
    $chargeFilter: ChargeFilterInput
  ) {
    listUsers(first: 1, filter: $filter) {
      edges {
        node {
          id
          walletTransactions(
            limit: 50
            sort: [{ field: CREATED_ON, order: DESC }]
            filter: $walletTransactionFilter
          ) {
            id
            oldId
            amount
            amountRefunded
            chargeTotal
            createdOn
            description
            type
            refunded
            taxAmount
            taxRate
          }
          charges(
            limit: 50
            sort: [{ field: CREATED, order: DESC }]
            filter: $chargeFilter
          ) {
            id
            amount
            amountRefunded
            chargeTotal
            created
            description
            holdAmount
            refundStatus
            status
            refunded
            taxAmount
            taxRate
          }
        }
      }
    }
  }
`;

export interface UserTransactionsData {
  walletTransactions: WalletTransactionGraphQL[];
  charges: ChargeGraphQL[];
}

/**
 * Builds the per-list filters for a user's transactions: the system or
 * organization scope AND'd with a search or-group over the fields each list
 * renders — description/type for wallet transactions, description/status for
 * charges. The or-groups stay at the top level of each filter — the API
 * rejects `or` nested inside relationship filters.
 */
export function buildUserTransactionsFilters(
  scope: { systemId?: string; organizationId?: string },
  search?: string
): {
  walletTransactionFilter: WalletTransactionFilterInput | undefined;
  chargeFilter: ChargeFilterInput | undefined;
} {
  const scopeFilter = scope.organizationId
    ? { organizationId: { eq: scope.organizationId } }
    : scope.systemId
    ? { systemId: { eq: scope.systemId } }
    : {};

  const walletTransactionFilter: WalletTransactionFilterInput = { ...scopeFilter };
  const chargeFilter: ChargeFilterInput = { ...scopeFilter };

  if (search) {
    const term = `%${search}%`;
    walletTransactionFilter.or = [
      { description: { ilike: term } },
      { type: { ilike: term } },
    ];
    chargeFilter.or = [{ description: { ilike: term } }, { status: { ilike: term } }];
  }

  return {
    walletTransactionFilter:
      Object.keys(walletTransactionFilter).length > 0
        ? walletTransactionFilter
        : undefined,
    chargeFilter: Object.keys(chargeFilter).length > 0 ? chargeFilter : undefined,
  };
}

async function fetchUserTransactions(
  phone: string,
  systemId?: string,
  organizationId?: string,
  search?: string
): Promise<UserTransactionsData> {
  const { walletTransactionFilter, chargeFilter } = buildUserTransactionsFilters(
    { systemId, organizationId },
    search
  );

  const result = await fetchQuery<UseUserTransactionsQueryQuery>(
    relayEnvironment,
    UserTransactionsQuery,
    {
      filter: {
        phone: { eq: phone },
      },
      walletTransactionFilter,
      chargeFilter,
    }
  ).toPromise();

  const node = result?.listUsers?.edges?.[0]?.node;
  return {
    walletTransactions: (node?.walletTransactions as WalletTransactionGraphQL[]) ?? [],
    charges: (node?.charges as ChargeGraphQL[]) ?? [],
  };
}

export function useUserTransactionsQuery(
  phone: string | undefined,
  options?: {
    enabled?: boolean;
    systemId?: string;
    organizationId?: string;
    search?: string;
  }
) {
  return useQuery({
    queryKey: [
      'userTransactions',
      phone,
      options?.systemId,
      options?.organizationId,
      options?.search,
    ],
    queryFn: () =>
      fetchUserTransactions(
        phone!,
        options?.systemId,
        options?.organizationId,
        options?.search
      ),
    enabled: options?.enabled !== false && Boolean(phone),
    // Keep showing the previous results while a new search refetches
    placeholderData: (previousData: UserTransactionsData | undefined) => previousData,
  });
}
